import API from 'utils/api';
import getFormattedValues from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';

export const baseURI = '/catalogos/administracion-de-plazas';

const getValuesAndConfig = (values) => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  const formattedValues = getFormattedValues(values, {
    formData: config.headers,
    clean: false,
  });
  return [formattedValues, config];
};

/**
 * GET Administracion de Plazas
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getAdministracionDePlazas = async (params) => {
  try {
    const response = await API.get(baseURI, { params });
    return response.data;
  // eslint-disable-next-line no-unreachable
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Administracion de Plaza
 * @param {number} id
 * @returns {Promise<object|null>}
 */
export const getAdministracionDePlaza = async (id) => {
  try {
    const response = await API.get(`${baseURI}/${id}`);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

/**
 * POST Administracion de Plaza
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postAdministracionDePlaza = async (values) => {
  try {
    const response = await API.post(baseURI, ...getValuesAndConfig(values));
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Administracion de Plaza
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchAdministracionDePlaza = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}/${id}`, ...getValuesAndConfig(values));
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE Administracion de Plaza
 * @param {number} id
 * @returns {Promise<boolean>}
 */
export const deleteAdministracionDePlaza = async (id) => {
  try {
    const response = await API.delete(`${baseURI}/${id}`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * DELETE permanente (hard-delete real, no borrado lógico) de una Administración
 * de Plaza ya Cancelada. A diferencia de `deleteAdministracionDePlaza` (borrado
 * lógico, bloqueado por el backend para registros Cancelados), este endpoint
 * solo acepta registros con estados_globales_id=5.
 * @param {number} id
 * @returns {Promise<boolean>}
 */
export const eliminarPermanenteAdministracionDePlaza = async (id) => {
  try {
    const response = await API.delete(`${baseURI}/${id}/eliminar-permanente`);
    onSuccess(response, 'Eliminado permanentemente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Plazas
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getAdministracionDePlazasvistaOptimized = async (params) => {
  try {
    const response = await API.get('/catalogos/asignacion-plazas-vista-optimized', { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Plaza optimizada por ID
 * @param {number} id
 * @returns {Promise<object | null>}
 */
export const getAdministracionDePlazavistaOptimized = async (id) => {
  try {
    const response = await API.get('/catalogos/asignacion-plazas-vista-optimized', {
      params: { id },
    });

    // Si devuelve un arreglo, filtramos el que coincida con el ID
    if (Array.isArray(response.data)) {
      return response.data.find((p) => p.id === id) || null;
    }

    return response.data || null;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * PUT Autorización Masiva de Plazas — despacha tarea Celery y devuelve {task_id, total}.
 * @param {Array} payload - Lista de objetos [{id: plazaId, observaciones: "..."}]
 * @returns {Promise<{task_id: string, total: number}>}
 */
export const putAutorizarPlazaMasiva = async (payload) => {
  const uri = '/catalogos/administracion-plazas-detalles/autorizar-masiva/';
  const response = await API.put(uri, payload);
  return response.data;
};

/**
 * GET catálogo de sujetos de nómina soportados (Empleado, Becario, Honorario Asimilado)
 * @returns {Promise<{slug: string, etiqueta: string}[]>}
 */
export const getSujetosDeNomina = async () => {
  try {
    const response = await API.get('/catalogos/sujetos-de-nomina/');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};
